import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';

import useDebounce from '@hooks/useDebounce';
import { type SearchParams, useSearchParams } from '@hooks/useSearchParams';
import useStoreAppConfig from '@hooks/useStoreAppConfig';
import { useGetHotels } from '@spazious/react-backoffice';

export type HotelOption = {
  value: string;
  label: string;
  slug?: string;
  hotelChain?: string;
  hotelLogo?: string;
};

/** Mirrors the `Hotel` shape stored in the `appConfig` Redux slice. */
export type SelectedHotel = {
  hotel?: string;
  hotelChain?: string;
  hotelName?: string;
  hotelSlug?: string;
  hotelLogo?: string;
};

/**
 * Shared data hook for the hotel multi-select used by `Header` (desktop) and
 * `HeaderMobile`. Owns:
 *   - hotel list fetching (with server search + paginated load-more)
 *   - selection state synced bidirectionally with the URL `?hotels` param
 *   - input value tracking for the search field
 *   - normalization of selection objects into `HotelOption` shape
 */
export function useHotelSelector() {
  const [{ selectedHotels, hotelChain }, { setSelectedHotels }] = useStoreAppConfig();
  const chainId = hotelChain?.hotelChainId;
  const [page, setPage] = useState(1);
  const [searchHotel, setSearchHotel] = useState<string | undefined>(undefined);
  const debounceSearchHotel = useDebounce(
    (value: string | undefined) => setSearchHotel(value),
    500
  );
  const [inputValue, setInputValue] = useState('');
  const [accumulatedHotels, setAccumulatedHotels] = useState<
    NonNullable<ReturnType<typeof useGetHotels>['data']>['data']
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // URL → Redux. Updates from the change handler also write to the URL, so the
  // sync is one-way here to avoid feedback loops.
  useEffect(() => {
    const urlHotels = searchParams?.hotels as SelectedHotel[] | undefined;
    if (urlHotels && !isEqual(urlHotels, selectedHotels)) {
      setSelectedHotels(urlHotels);
    } else if (!urlHotels && selectedHotels && selectedHotels.length > 0) {
      setSearchParams({ hotels: selectedHotels as unknown as SearchParams[string] });
    }
  }, [searchParams, selectedHotels, setSearchParams, setSelectedHotels]);

  const [prevChainId, setPrevChainId] = useState(chainId);
  if (chainId !== prevChainId) {
    setPrevChainId(chainId);
    if (page !== 1) setPage(1);
    if (accumulatedHotels.length > 0) setAccumulatedHotels([]);
  }

  const hotelsQueryResult = useGetHotels({
    variables: { pageNumber: page, filters: { search: searchHotel, hotelChain: chainId } },
    refetchOnWindowFocus: false,
  });

  const fetchedHotelOptions: HotelOption[] = useMemo(
    () =>
      [...accumulatedHotels, ...(hotelsQueryResult?.data?.data ?? [])].map(
        ({ attributes, id, relationships }) => ({
          value: id,
          label: attributes?.name ?? '',
          slug: attributes?.slug,
          hotelChain: relationships?.hotelChain?.data?.id,
          hotelLogo: attributes?.logoPositive?.thumbnailRetina,
        })
      ),
    [accumulatedHotels, hotelsQueryResult?.data?.data]
  );

  /** Selected hotels mapped to the option shape consumed by `Autocomplete`. */
  const value: HotelOption[] = useMemo(
    () =>
      (selectedHotels ?? [])
        .filter((hotel) => hotel.hotel)
        .map((hotel) => ({
          value: hotel.hotel ?? '',
          hotelChain: hotel.hotelChain,
          label: hotel.hotelName ?? '',
          slug: hotel.hotelSlug,
          hotelLogo: hotel.hotelLogo,
        })),
    [selectedHotels]
  );

  // Selected hotels are pinned to the top of the list so the current selection
  // is visible as soon as the dropdown opens, without scrolling to find it.
  // Skipped while a search is active: there the list must show the hotels
  // matching the query and nothing else.
  const hotelOptions: HotelOption[] = useMemo(() => {
    if (searchHotel) return fetchedHotelOptions;

    const selectedIds = new Set(value.map((option) => option.value));

    return [...value, ...fetchedHotelOptions.filter((option) => !selectedIds.has(option.value))];
  }, [fetchedHotelOptions, searchHotel, value]);

  const handleChange = useCallback(
    (options: HotelOption | HotelOption[]) => {
      const optionsArray = Array.isArray(options) ? options : [options];
      const hotelContexts: SelectedHotel[] = optionsArray.map((option) => ({
        hotelSlug: option?.slug,
        hotelName: option?.label,
        hotel: option?.value,
        hotelChain: option?.hotelChain,
        hotelLogo: option?.hotelLogo,
      }));
      setSelectedHotels(hotelContexts);
      // `page: 0` goes with the scope change. Re-scoping invalidates the page
      // index — a page that was in range for the previous selection usually is
      // not in the new one, so keeping it makes the next request ask for a page
      // that may not exist and the list comes back empty. This is the only place
      // that knows the change came from the user: the URL → Redux effect above
      // reconciles the same value on startup, and a list page watching either
      // one cannot tell the two apart.
      setSearchParams({
        hotels: hotelContexts as unknown as SearchParams[string],
        page: 0,
      });
    },
    [setSearchParams, setSelectedHotels]
  );

  const handleInputChange = useCallback(
    (value: string | undefined) => {
      setAccumulatedHotels([]);
      setPage(1);
      debounceSearchHotel(value || undefined);
    },
    [debounceSearchHotel]
  );

  const handleLoadMore = useCallback(() => {
    setAccumulatedHotels((prev) => [...prev, ...(hotelsQueryResult?.data?.data ?? [])]);
    setPage((prev) => prev + 1);
  }, [hotelsQueryResult?.data?.data]);

  // The runtime response carries a non-standard `meta.pagination.pages`
  // counter that isn't on the typed `BackofficeGetHotelsQueryData`; narrow
  // here instead of casting to `any`.
  const meta = (
    hotelsQueryResult?.data as { meta?: { pagination?: { pages?: number } } } | undefined
  )?.meta;
  const shouldLoadMore = (meta?.pagination?.pages ?? 0) > page;

  return {
    selectedHotels: selectedHotels as SelectedHotel[] | null,
    hotelOptions,
    value,
    inputValue,
    setInputValue,
    handleChange,
    handleInputChange,
    handleLoadMore,
    shouldLoadMore,
    isLoading: hotelsQueryResult?.loading,
  };
}
